// MultiSelect — a styled, accessible multi-value dropdown, the multi-select
// sibling of InlineSelect (same trigger/panel styling). Reusable app-wide; built
// for the job-board competency filter but intentionally generic. Selecting an
// option toggles it and keeps the panel open; the trigger shows a count.
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { InlineSelectOption } from './InlineSelect';

interface MultiSelectProps {
    values: string[];
    onChange: (values: string[]) => void;
    options: InlineSelectOption[];
    placeholder?: string;
    icon?: LucideIcon;
    disabled?: boolean;
    className?: string;
    ariaLabel?: string;
    /** How the trigger summarises a selection. Default: "N valda". */
    summarise?: (count: number) => string;
}

const TRIGGER =
    'w-full flex items-center gap-2 text-sm text-left transition-colors bg-white text-gray-900 ' +
    'border border-gray-200 rounded-lg px-2.5 py-1.5 hover:border-gray-300 focus:outline-none ' +
    'focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed';

export const MultiSelect: React.FC<MultiSelectProps> = ({
    values,
    onChange,
    options,
    placeholder = 'Välj…',
    icon: Icon,
    disabled,
    className,
    ariaLabel,
    summarise = (n) => `${n} valda`,
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const selectedSet = new Set(values);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const toggle = (value: string) => {
        if (selectedSet.has(value)) onChange(values.filter((v) => v !== value));
        else onChange([...values, value]);
    };

    return (
        <div ref={rootRef} className={`relative ${className || ''}`}>
            <button
                type="button"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={() => !disabled && setOpen((v) => !v)}
                className={TRIGGER}
            >
                {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                <span className={`flex-1 min-w-0 truncate ${values.length ? '' : 'text-gray-400'}`}>
                    {values.length ? summarise(values.length) : placeholder}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <ul
                    role="listbox"
                    aria-multiselectable
                    className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg min-w-[12rem]"
                >
                    {options.length === 0 && (
                        <li className="px-3 py-2 text-sm text-gray-400">Inga alternativ</li>
                    )}
                    {options.map((opt) => {
                        const isSel = selectedSet.has(opt.value);
                        return (
                            <li key={opt.value} role="option" aria-selected={isSel}>
                                <button
                                    type="button"
                                    onClick={() => toggle(opt.value)}
                                    className={`w-full flex items-start gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[#A4C3B2]/20 ${
                                        isSel ? 'text-[#1A4D4F] font-medium' : 'text-gray-700'
                                    }`}
                                >
                                    <span className={`w-4 h-4 mt-0.5 flex-shrink-0 rounded border flex items-center justify-center ${
                                        isSel ? 'bg-[#1A4D4F] border-[#1A4D4F]' : 'border-gray-300'
                                    }`}>
                                        {isSel && <Check className="w-3 h-3 text-white" />}
                                    </span>
                                    <span className="min-w-0 block truncate">{opt.label}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default MultiSelect;
